import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import AnalyticsTopBar from "../components/AnalyticsTopBar";
import FlowVisualization from "../components/FlowVisualization";
import QueryBuilderPanel from "../components/QueryBuilderPanel";
import EventsTimeFilter from "../../../conversion-events/components/EventsTimeFilter";
import { cryptiqueAnalyticsTheme as theme } from "../theme";
import { useAnalytics } from "../context/AnalyticsContext";
import { useFlow } from "../hooks/useFlow";
import { useEventsMetadata, useCards } from "../hooks/useInsights";
import { useBoards } from "../hooks/useBoards";
import * as analyticsApi from "../api/analyticsApi";

const EVENTS_PRESET_TO_ANALYTICS = {
  today: "Today",
  yesterday: "Yesterday",
  "7d": "7D",
  "30d": "30D",
  "3m": "3M",
  "6m": "6M",
  "12m": "12M",
};

function analyticsPresetToEventsPreset(p) {
  if (!p) return "30d";
  const map = { Today: "today", Yesterday: "yesterday", "7D": "7d", "30D": "30d", "3M": "3m", "6M": "6m", "12M": "12m" };
  return map[p] || "30d";
}

function buildFlowDSL(anchorEvents, conversionWindow, conversionCriteria, globalFilters, breakdown, timeRange) {
  return {
    version: 1,
    queryType: "flows",
    time: timeRange,
    definition: {
      anchorEvents: anchorEvents
        .filter((a) => a.eventName && a.eventName.trim())
        .map((a) => ({
          id: a.id,
          eventName: a.eventName.trim(),
          stepsBefore: a.stepsBefore || 3,
          stepsAfter: a.stepsAfter || 3,
          filters: (a.filters || []).filter((f) => f.category !== "first_time"),
        })),
      conversionWindow,
      conversionCriteria,
      topN: 3,
      filters: (globalFilters || []).map((f) => ({
        property: f.field ?? f.property,
        operator: f.operator,
        value: f.value,
        value2: f.value2,
        propertyType: f.propertyType,
        propertySource: f.propertySource,
        category: f.category,
      })),
      breakdown: breakdown || null,
    },
    visualization: { chartType: "flow-sankey", config: {} },
  };
}

export default function FlowEditorPage() {
  const navigate = useNavigate();
  const { team, boardId } = useParams();
  const [searchParams] = useSearchParams();
  const cardIdParam = searchParams.get("cardId");
  const rowIndexParam = searchParams.get("rowIndex");
  const rowIndex = rowIndexParam != null && rowIndexParam !== "" ? parseInt(rowIndexParam, 10) : undefined;

  const selectedTeamData = JSON.parse(localStorage.getItem("selectedTeamData") || "null");
  const teamId = selectedTeamData?._id ?? selectedTeamData?.id ?? null;
  const teamIdOrName = teamId || team;

  const { globalTimeRange, setGlobalTimeRange } = useAnalytics();
  const { result, loading, error, execute, clearResult } = useFlow();
  const { events, loadEvents } = useEventsMetadata();
  const { createBoard } = useBoards();
  const effectiveBoardId = boardId && boardId !== "new" ? boardId : null;
  const { cards, loadCards, updateCard } = useCards(effectiveBoardId, teamId);
  const editingCard = cardIdParam && cards.find((c) => String(c.id) === String(cardIdParam));

  const [boardName, setBoardName] = useState("");
  const [cardTitle, setCardTitle] = useState("Untitled Flow");
  const [insightTimeFilter, setInsightTimeFilter] = useState(null);
  const [activeTab, setActiveTab] = useState("query");
  const [globalFilters, setGlobalFilters] = useState([]);
  const [breakdown, setBreakdown] = useState(null);
  const [activeBreakdown, setActiveBreakdown] = useState("__all__");
  const [anchorEvents, setAnchorEvents] = useState([
    { id: "anchor_1", eventName: "", label: "", stepsBefore: 3, stepsAfter: 3, filters: [] },
  ]);
  const [conversionWindow, setConversionWindow] = useState({ value: 7, unit: "days" });
  const [conversionCriteria, setConversionCriteria] = useState("distinct_id");

  const hydratedRef = useRef(null);
  const executeTimeoutRef = useRef(null);

  // Load board name
  useEffect(() => {
    if (!boardId || boardId === "new") { setBoardName(""); return; }
    let cancelled = false;
    analyticsApi.fetchBoard(boardId, false, teamId).then((res) => {
      if (!cancelled) setBoardName((res?.data ?? res)?.name || "Board");
    }).catch(() => { if (!cancelled) setBoardName("Board"); });
    return () => { cancelled = true; };
  }, [boardId, teamId]);

  // Load events metadata
  useEffect(() => {
    if (loadEvents && teamIdOrName) {
      loadEvents(teamIdOrName).catch((err) => console.error("FlowEditorPage: loadEvents error", err));
    }
  }, [loadEvents, teamIdOrName]);

  // Hydrate from existing card
  useEffect(() => {
    if (!cardIdParam || !editingCard) return;
    if (hydratedRef.current === cardIdParam) return;
    hydratedRef.current = cardIdParam;

    const q = editingCard.queryDsl && typeof editingCard.queryDsl === "object" ? editingCard.queryDsl : {};
    setCardTitle(editingCard.title || "Untitled Flow");

    if (q.definition?.anchorEvents) {
      setAnchorEvents(q.definition.anchorEvents.map((a, i) => ({
        id: a.id || `anchor_${i + 1}`,
        eventName: a.eventName || "",
        label: a.eventName || "",
        stepsBefore: a.stepsBefore || 3,
        stepsAfter: a.stepsAfter || 3,
        filters: a.filters || [],
      })));
    }
    if (q.definition?.conversionWindow) setConversionWindow(q.definition.conversionWindow);
    if (q.definition?.conversionCriteria) setConversionCriteria(q.definition.conversionCriteria);
    if (q.definition?.filters) {
      setGlobalFilters(q.definition.filters.map((f, i) => ({
        ...f, id: f.id || `gf-${i}`, connector: i === 0 ? "WHERE" : "AND",
      })));
    }
    if (q.definition?.breakdown) setBreakdown(q.definition.breakdown);

    const t = q.time;
    if (t?.start && t?.end) {
      setInsightTimeFilter({ type: "custom", from: t.start, to: t.end });
    } else if (t?.preset) {
      setInsightTimeFilter(null);
      setGlobalTimeRange(t.preset);
    }
  }, [cardIdParam, editingCard, setGlobalTimeRange]);

  // Reset active breakdown pill when result changes
  useEffect(() => { setActiveBreakdown("__all__"); }, [result]);

  // Auto-execute (debounced)
  useEffect(() => {
    const validAnchors = anchorEvents.filter((a) => a.eventName && a.eventName.trim());
    if (validAnchors.length === 0) { clearResult(); return; }

    const timeRange = getTimeRange();
    const dsl = buildFlowDSL(validAnchors, conversionWindow, conversionCriteria, globalFilters, breakdown, timeRange);

    if (executeTimeoutRef.current) clearTimeout(executeTimeoutRef.current);
    executeTimeoutRef.current = setTimeout(() => {
      executeTimeoutRef.current = null;
      execute(dsl, teamIdOrName);
    }, 400);

    return () => {
      if (executeTimeoutRef.current) { clearTimeout(executeTimeoutRef.current); executeTimeoutRef.current = null; }
    };
  }, [anchorEvents, conversionWindow, conversionCriteria, globalFilters, breakdown, globalTimeRange, insightTimeFilter, teamIdOrName]); // eslint-disable-line

  function getTimeRange() {
    if (insightTimeFilter?.type === "preset" && insightTimeFilter.preset) {
      return { preset: EVENTS_PRESET_TO_ANALYTICS[insightTimeFilter.preset] || globalTimeRange || "30D", start: null, end: null, interval: "day", timezone: "UTC" };
    }
    if (insightTimeFilter?.from && insightTimeFilter?.to) {
      return { preset: null, start: insightTimeFilter.from, end: insightTimeFilter.to, interval: "day", timezone: "UTC" };
    }
    return { preset: globalTimeRange || "30D", start: null, end: null, interval: "day", timezone: "UTC" };
  }

  const handleSave = async () => {
    const validAnchors = anchorEvents.filter((a) => a.eventName && a.eventName.trim());
    if (validAnchors.length === 0) { alert("Please select at least one event"); return; }

    try {
      const dsl = buildFlowDSL(validAnchors, conversionWindow, conversionCriteria, globalFilters, breakdown, getTimeRange());

      if (cardIdParam && boardId && editingCard) {
        await updateCard(cardIdParam, {
          title: cardTitle.trim() || "Untitled Flow",
          queryDsl: dsl,
          visualization: { chartType: "flow-sankey", config: {} },
        });
        navigate(`/${team}/analytics/boards/${boardId}`);
        return;
      }

      let targetBoardId = boardId;
      if (!targetBoardId || targetBoardId === "new") {
        const newBoard = await createBoard({
          name: cardTitle || "Flow Board",
          description: "",
          time_range: { preset: globalTimeRange, interval: "day", timezone: "UTC" },
          filters: null, compare_mode: null, layout: null,
        }, teamId);
        targetBoardId = String(newBoard.id);
      }

      const position = typeof rowIndex === "number" ? { rowIndex, order: 0, w: 1 } : undefined;
      await analyticsApi.createCard(targetBoardId, {
        cardType: "flow",
        title: cardTitle,
        queryDsl: dsl,
        visualization: { chartType: "flow-sankey", config: {} },
        ...(position && { position }),
      }, teamId);

      navigate(`/${team}/analytics/boards/${targetBoardId}`);
    } catch (err) {
      console.error("FlowEditorPage: save failed", err);
      alert("Failed to save. Please try again.");
    }
  };

  const handleClose = () => navigate(`/${team}/analytics/boards/${boardId || "new"}`);

  const breadcrumbs = [
    "Boards",
    boardId && boardId !== "new"
      ? { label: boardName || "Board", path: `/${team}/analytics/boards/${boardId}` }
      : boardName || "Board",
    cardTitle || "Untitled Flow",
  ];

  const hasAnchor = anchorEvents.some((a) => a.eventName && a.eventName.trim());

  return (
    <div className="flex flex-col flex-1 min-h-0" style={{ backgroundColor: "#ffffff" }}>
      <AnalyticsTopBar
        breadcrumbs={breadcrumbs}
        showCreateNew={false}
        showTimeRange={false}
        showSaveClose={true}
        onSave={handleSave}
        onClose={handleClose}
      />

      {/* Sub-header: date filter */}
      <div
        className="flex items-center justify-between border-b px-4 py-2 gap-4"
        style={{ borderColor: theme.border, fontFamily: "Raleway, sans-serif" }}
      >
        <EventsTimeFilter
          selectedPreset={
            insightTimeFilter
              ? insightTimeFilter.type === "custom" ? "custom" : (insightTimeFilter.preset || "30d")
              : analyticsPresetToEventsPreset(globalTimeRange)
          }
          onTimeFilterChange={(filter) => {
            setInsightTimeFilter(filter);
            if (filter.type === "preset" && filter.preset) {
              setGlobalTimeRange(EVENTS_PRESET_TO_ANALYTICS[filter.preset] || globalTimeRange);
            }
          }}
        />
      </div>

      {/* Main layout: chart area + right panel */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Chart area */}
        <div className="flex-1 min-w-0 overflow-auto p-6">
          {/* Card title */}
          <div className="mb-4">
            <input
              type="text"
              value={cardTitle}
              onChange={(e) => setCardTitle(e.target.value)}
              className="text-lg font-medium bg-transparent border-b outline-none"
              style={{ color: theme.textPrimary, borderColor: theme.border, fontFamily: "Raleway, sans-serif" }}
              placeholder="Flow title"
            />
          </div>

          {!hasAnchor ? (
            <div
              className="flex flex-col items-center justify-center h-64 rounded-xl border border-dashed text-sm"
              style={{ borderColor: theme.border, color: theme.textMuted }}
            >
              <p className="font-medium mb-1" style={{ color: theme.textSecondary }}>Select an event to see the flow</p>
              <p>Use the Query panel on the right to configure your flow.</p>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center h-64 text-sm" style={{ color: theme.textMuted }}>
              Loading flow data…
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64 text-sm" style={{ color: "#ef4444" }}>
              Error: {error}
            </div>
          ) : result ? (
            <div className="rounded-xl border overflow-hidden" style={{ borderColor: theme.border }}>
              {result.breakdown?.values?.length > 0 && (
                <div className="flex gap-2 px-4 pt-3 pb-1 flex-wrap border-b" style={{ borderColor: theme.border }}>
                  {["__all__", ...result.breakdown.values].map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setActiveBreakdown(v)}
                      className="px-3 py-1 rounded-full text-xs transition-colors"
                      style={{
                        background: activeBreakdown === v ? (theme.accent || "#7c3aed") : (theme.accentSoft || "#f3f0ff"),
                        color: activeBreakdown === v ? "#fff" : (theme.textPrimary || "#111"),
                      }}
                    >
                      {v === "__all__" ? "All" : v}
                    </button>
                  ))}
                </div>
              )}
              <FlowVisualization
                data={
                  activeBreakdown === "__all__" || !result.breakdown
                    ? result
                    : result.breakdown.data[activeBreakdown]
                      ? { ...result, ...result.breakdown.data[activeBreakdown] }
                      : result
                }
                height={520}
              />
            </div>
          ) : null}
        </div>

        {/* Right panel via QueryBuilderPanel */}
        <QueryBuilderPanel
          activeTab={activeTab}
          onTabChange={setActiveTab}
          reportType="flow"
          flowAnchorEvents={anchorEvents}
          onFlowAnchorEventsChange={setAnchorEvents}
          flowConversionWindow={conversionWindow}
          onFlowConversionWindowChange={setConversionWindow}
          flowConversionCriteria={conversionCriteria}
          onFlowConversionCriteriaChange={setConversionCriteria}
          availableEvents={events}
          teamId={teamIdOrName}
          globalFilters={globalFilters}
          onGlobalFiltersChange={setGlobalFilters}
          breakdowns={breakdown ? [breakdown] : []}
          onBreakdownsChange={(arr) => setBreakdown(arr[0] || null)}
        />
      </div>
    </div>
  );
}
